import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { v4 as uuidV4 } from "uuid";
import { firestore, storage, FieldValue } from "../../config/firebaseConfig";

// Rebuild records pivoted onto Firestore (`rebuildLogs`): one document per
// action — full BOM rebuilds and quick actions (valve repack, greasing,
// bonnet repack…) share the collection, distinguished by `action`. The
// legacy RTDB `logs/rebuild/items` and the Excel Rebuild Log both import
// here; form TEMPLATES stay at RTDB `forms/rebuild/forms` for now.

// Asset families a rebuild form can belong to, in the order they appear in
// the Form Builder's picker, the Forms tab and the Add dialog. Shared so the
// three can't drift: the Forms tab filters strictly by this list, so a form
// in a category missing from it would be invisible there.
export const REBUILD_CATEGORIES = [
  "Gate Valves",
  "Ball Valves",
  "Check Valves",
  "Plug Valves",
  "Relief Valves",
  "Swivels/Loops",
  "Chokes",
  "Wireline",
  "Misc.",
];

// Inspection scales used by the wireline maintenance sheets (QF-900+).
// Those forms aren't parts-traceability documents like the QF-710 rebuild
// forms — they're checklists, and each section asks its question on a
// different two-way scale.
export const CHECK_SCALES = {
  okDamaged: {
    label: "OK / Damaged",
    options: [
      { value: "ok", label: "OK" },
      { value: "damaged", label: "Damaged" },
    ],
  },
  yesNo: {
    label: "Yes / No",
    options: [
      { value: "yes", label: "Yes" },
      { value: "no", label: "No" },
    ],
  },
  yesDamaged: {
    label: "Yes / Damaged",
    options: [
      { value: "yes", label: "Yes" },
      { value: "damaged", label: "Damaged" },
    ],
  },
  passFail: {
    label: "Pass / Fail",
    options: [
      { value: "pass", label: "Pass" },
      { value: "fail", label: "Fail" },
    ],
  },
  tolerance: {
    label: "In / Out of Tolerance",
    options: [
      { value: "in", label: "In Tolerance" },
      { value: "out", label: "Out of Tolerance" },
    ],
  },
};

export const DEFAULT_CHECK_SCALE = "okDamaged";

export const checkScaleOptions = (scale) =>
  (CHECK_SCALES[scale] || CHECK_SCALES[DEFAULT_CHECK_SCALE]).options;

export const REBUILD_ACTIONS = {
  rebuild: "Full Rebuild",
  valveRepack: "Valve Repack",
  swivelRepack: "Swivel/Loop Repack",
  greasing: "Greasing",
  bonnetRepack: "Bonnet Repack",
  manifoldRebuild: "Manifold",
  // Inspection/maintenance sheets — the wireline QF-900 series and any
  // future checklist form. Not rebuild-grade: no BOM or exploded view
  // required, and serial/location stay optional.
  maintenance: "Maintenance",
  other: "Other Action",
};

// Repacks replace seals/packing with traceable parts — rebuild-grade work
// documented on the same controlled forms as full rebuilds. These actions
// get the full-rebuild rules: BOM + exploded view required on templates;
// serial + location required on template-backed records.
export const FULL_REBUILD_ACTIONS = [
  "rebuild",
  "valveRepack",
  "swivelRepack",
  "bonnetRepack",
];

// A repack is a SCOPE on a form's BOM, not a separate template: rows carry
// a `section` tag naming the repack that includes them (e.g. on a gate
// valve form, rows above the bonnet seal ring are tagged bonnetRepack and
// the gate & seat assembly rows valveRepack). Full rebuild = every row.
export const REPACK_ACTIONS = ["bonnetRepack", "valveRepack", "swivelRepack"];

export const formSupportsAction = (form, action) => {
  if (!action || action === (form?.action || "rebuild")) return true;
  return (
    REPACK_ACTIONS.includes(action) &&
    (form?.bom || []).some((row) => row.section === action)
  );
};

export const bomRowsForAction = (form, action) => {
  const rows = form?.bom || [];
  if (!action || action === (form?.action || "rebuild")) return rows;
  if (REPACK_ACTIONS.includes(action))
    return rows.filter((row) => row.section === action);
  return rows;
};

let unsubscribe = null;
let currentLimit = 0;

export const DEFAULT_REBUILD_WINDOW = 1000;
export const REBUILD_WINDOW_STEP = 2000;
const SEARCH_CAP = 5000;

export const fetchRebuildLogsFsAsync = createAsyncThunk(
  "rebuildLogs/fetch",
  async (limitArg, thunkAPI) => {
    const limit = limitArg || DEFAULT_REBUILD_WINDOW;
    if (unsubscribe && limit <= currentLimit) return;
    if (unsubscribe) unsubscribe();
    currentLimit = limit;
    thunkAPI.dispatch(setLoading(true));
    unsubscribe = firestore
      .collection("rebuildLogs")
      .orderBy("loggedAt", "desc")
      .limit(limit)
      .onSnapshot(
        (snapshot) => {
          const items = {};
          snapshot.forEach((doc) => {
            const data = doc.data();
            if (!data.deleted) items[doc.id] = data;
          });
          thunkAPI.dispatch(
            setRebuildLogs({
              items,
              windowSize: limit,
              reachedEnd: snapshot.size < limit,
            })
          );
        },
        (error) => {
          console.error("rebuildLogs listener error", error);
          thunkAPI.dispatch(setError(error.message));
        }
      );
  }
);

export const cancelFetchRebuildLogsFs = createAsyncThunk(
  "rebuildLogs/cancel",
  async () => {
    if (unsubscribe) {
      unsubscribe();
      unsubscribe = null;
      currentLimit = 0;
    }
  }
);

// Full-history search: asset and action are equality filters (multiple
// equalities need no composite index); a date range alone orders on the
// `date` string. Secondary criteria narrow client-side.
export const rebuildSearchMatch = (
  data,
  { assetNumber = "", action = "", dateFrom = "", dateTo = "" }
) => {
  if (data.deleted) return false;
  const gps = assetNumber.trim();
  if (gps && String(data.assetNumber || "") !== gps) return false;
  if (action && data.action !== action) return false;
  if (dateFrom && (data.date || "") < dateFrom) return false;
  if (dateTo && (data.date || "") > dateTo) return false;
  return true;
};

export const searchRebuildLogsAsync = createAsyncThunk(
  "rebuildLogs/search",
  async (criteria, thunkAPI) => {
    try {
      const gps = (criteria.assetNumber || "").trim();
      const { action = "", dateFrom = "", dateTo = "" } = criteria;
      let query = firestore.collection("rebuildLogs");
      let capped = false;
      if (gps) {
        query = query.where("assetNumber", "==", gps);
        if (action) query = query.where("action", "==", action);
      } else if (action) {
        query = query.where("action", "==", action).limit(SEARCH_CAP);
      } else if (dateFrom || dateTo) {
        if (dateFrom) query = query.where("date", ">=", dateFrom);
        if (dateTo) query = query.where("date", "<=", dateTo);
        query = query.orderBy("date", "desc").limit(SEARCH_CAP);
      } else {
        return thunkAPI.rejectWithValue("Enter something to search for");
      }
      const snapshot = await query.get();
      capped = !gps && snapshot.size >= SEARCH_CAP;
      const items = {};
      snapshot.forEach((doc) => {
        const data = doc.data();
        if (rebuildSearchMatch(data, criteria)) items[doc.id] = data;
      });
      return { items, capped };
    } catch (error) {
      console.error("searchRebuildLogs failed", error);
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

// Attachments: Storage upload, standard shape, files never deleted by app.
const uploadRebuildFiles = async (files) => {
  const uploads = (files || []).map(async (file) => {
    const filePath = `rebuild/attachments/${uuidV4()}/${file.name}`;
    const snap = await storage.ref(filePath).put(file);
    const downloadUrl = await snap.ref.getDownloadURL();
    return { filePath, downloadUrl, fileName: file.name };
  });
  return Promise.all(uploads);
};

// Persist one record. `values` carries the runner's full form state; new
// files in `unsavedFiles` upload first and merge into `files`.
export const saveRebuildRecordAsync = createAsyncThunk(
  "rebuildLogs/save",
  async ({ id, values, user }, thunkAPI) => {
    try {
      const uploaded = await uploadRebuildFiles(values.unsavedFiles);
      const ref = id
        ? firestore.collection("rebuildLogs").doc(id)
        : firestore.collection("rebuildLogs").doc();
      const { unsavedFiles, ...entry } = values;
      await ref.set(
        {
          ...entry,
          assetNumber: String(values.assetNumber || values.asset || "").trim(),
          files: [...(values.files || []), ...uploaded],
          deleted: false,
          loggedAt: values.loggedAt || FieldValue.serverTimestamp(),
          loggedBy: values.loggedBy || {
            uid: user?.uid || "",
            name: user?.name || "",
          },
          updatedAt: FieldValue.serverTimestamp(),
        },
        { merge: true }
      );
      return { id: ref.id };
    } catch (error) {
      console.error("saveRebuildRecord failed", error);
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

// The paper forms all close with a second-person "Verified By" sign-off;
// digital records mirror it as a lightweight stamp separate from editing.
export const verifyRebuildRecordAsync = createAsyncThunk(
  "rebuildLogs/verify",
  async ({ id, user, remove }, thunkAPI) => {
    try {
      await firestore
        .collection("rebuildLogs")
        .doc(id)
        .set(
          remove
            ? {
                verifiedBy: null,
                verifiedAt: null,
                updatedAt: FieldValue.serverTimestamp(),
              }
            : {
                verifiedBy: {
                  uid: user?.uid || "",
                  name: user?.name || "",
                },
                verifiedAt: FieldValue.serverTimestamp(),
                updatedAt: FieldValue.serverTimestamp(),
              },
          { merge: true }
        );
      return { id };
    } catch (error) {
      console.error("verifyRebuildRecord failed", error);
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

export const deleteRebuildRecordsFsAsync = createAsyncThunk(
  "rebuildLogs/delete",
  async (ids, thunkAPI) => {
    try {
      const batch = firestore.batch();
      ids.forEach((id) =>
        batch.set(
          firestore.collection("rebuildLogs").doc(id),
          { deleted: true, updatedAt: FieldValue.serverTimestamp() },
          { merge: true }
        )
      );
      await batch.commit();
      return ids;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

const rebuildLogsSlice = createSlice({
  name: "rebuildLogs",
  initialState: {
    items: {},
    error: null,
    saving: false,
    loading: false,
    windowSize: 0,
    reachedEnd: true,
    search: { items: null, searching: false, capped: false, error: null },
  },
  reducers: {
    setRebuildLogs(state, action) {
      state.items = action.payload.items;
      state.windowSize = action.payload.windowSize;
      state.reachedEnd = action.payload.reachedEnd;
      state.loading = false;
      state.error = null;
    },
    setLoading(state, action) {
      state.loading = action.payload;
    },
    setError(state, action) {
      state.error = action.payload;
      state.loading = false;
    },
    clearRebuildSearch(state) {
      state.search = {
        items: null,
        searching: false,
        capped: false,
        error: null,
      };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(searchRebuildLogsAsync.pending, (state) => {
        state.search.searching = true;
        state.search.error = null;
      })
      .addCase(searchRebuildLogsAsync.fulfilled, (state, action) => {
        state.search.searching = false;
        state.search.items = action.payload.items;
        state.search.capped = action.payload.capped;
      })
      .addCase(searchRebuildLogsAsync.rejected, (state, action) => {
        state.search.searching = false;
        state.search.error = action.payload;
      })
      .addCase(saveRebuildRecordAsync.pending, (state) => {
        state.saving = true;
      })
      .addCase(saveRebuildRecordAsync.fulfilled, (state) => {
        state.saving = false;
      })
      .addCase(saveRebuildRecordAsync.rejected, (state, action) => {
        state.saving = false;
        state.error = action.payload;
      });
  },
});

export const { setRebuildLogs, setLoading, setError, clearRebuildSearch } =
  rebuildLogsSlice.actions;

export default rebuildLogsSlice.reducer;
